import { useEffect, useRef } from "react";

const MAX_PARTICLES = 55;
const PARTICLE_SIZE_DIVISOR = 28;
const CONNECT_DISTANCE = 130;
const MOUSE_DISTANCE = 180;
const COLORS = ["15,118,110", "201,162,39"]; // teal, gold

export default function ParticleCanvas({ enabled = true }) {
  const canvasRef = useRef(null);
  const stateRef = useRef({
    particles: [],
    mouse: { x: -9999, y: -9999 },
    raf: 0,
    dpr: 1,
  });

  useEffect(() => {
    if (!enabled) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const state = stateRef.current;
    state.dpr = Math.min(window.devicePixelRatio || 1, 2);

    /* ---------- resize ---------- */
    const resize = () => {
      const { innerWidth: w, innerHeight: h } = window;
      canvas.width = w * state.dpr;
      canvas.height = h * state.dpr;
      canvas.style.width = w + "px";
      canvas.style.height = h + "px";
      ctx.setTransform(state.dpr, 0, 0, state.dpr, 0, 0);
      initParticles(w, h);
    };

    /* ---------- particles ---------- */
    const initParticles = (w, h) => {
      const count = Math.min(MAX_PARTICLES, Math.floor(w / PARTICLE_SIZE_DIVISOR));
      state.particles = Array.from({ length: count }, () => ({
        x: Math.random() * w,
        y: Math.random() * h,
        vx: (Math.random() - 0.5) * 0.5,
        vy: (Math.random() - 0.5) * 0.5,
        r: Math.random() * 1.6 + 0.4,
        c: COLORS[Math.random() > 0.5 ? 0 : 1],
      }));
    };

    /* ---------- mouse ---------- */
    const onMouseMove = (e) => {
      state.mouse.x = e.clientX;
      state.mouse.y = e.clientY;
    };
    const onMouseLeave = () => {
      state.mouse.x = -9999;
      state.mouse.y = -9999;
    };

    /* ---------- draw loop ---------- */
    const draw = () => {
      const { innerWidth: w, innerHeight: h } = window;
      const isDark = document.documentElement.classList.contains("dark");
      const { particles, mouse } = state;

      ctx.clearRect(0, 0, w, h);

      for (let i = 0; i < particles.length; i++) {
        const p = particles[i];

        // حرکت
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0 || p.x > w) p.vx *= -1;
        if (p.y < 0 || p.y > h) p.vy *= -1;

        // خودِ ذره
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${p.c}, ${isDark ? 0.6 : 0.45})`;
        ctx.fill();

        // اتصال بین ذرات
        for (let j = i + 1; j < particles.length; j++) {
          const q = particles[j];
          const dx = p.x - q.x;
          const dy = p.y - q.y;
          const d = Math.hypot(dx, dy);
          if (d < CONNECT_DISTANCE) {
            ctx.beginPath();
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(q.x, q.y);
            ctx.strokeStyle = `rgba(20, 184, 166, ${(1 - d / CONNECT_DISTANCE) * (isDark ? 0.15 : 0.1)
              })`;
            ctx.lineWidth = 0.6;
            ctx.stroke();
          }
        }

        // اتصال به موس
        const dm = Math.hypot(p.x - mouse.x, p.y - mouse.y);
        if (dm < MOUSE_DISTANCE) {
          ctx.beginPath();
          ctx.moveTo(p.x, p.y);
          ctx.lineTo(mouse.x, mouse.y);
          ctx.strokeStyle = `rgba(201, 162, 39, ${(1 - dm / MOUSE_DISTANCE) * 0.28
            })`;
          ctx.lineWidth = 0.8;
          ctx.stroke();
        }
      }

      state.raf = requestAnimationFrame(draw);
    };

    /* ---------- init ---------- */
    resize();
    window.addEventListener("resize", resize);
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseleave", onMouseLeave);
    state.raf = requestAnimationFrame(draw);

    /* ---------- cleanup ---------- */
    return () => {
      cancelAnimationFrame(state.raf);
      window.removeEventListener("resize", resize);
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseleave", onMouseLeave);
    };
  }, [enabled]);

  if (!enabled) return null;

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 h-full w-full opacity-50"
    />
  );
}